import { useState } from 'react';
import {
  claimCluster,
  dismissCluster,
  listClusters,
  type DeviceOnlyMember,
} from '../api/endpoints.js';
import { useAction, useApi } from '../api/hooks.js';
import {
  Async,
  Badge,
  Button,
  Failure,
  Field,
  Note,
  Panel,
  Raw,
  Row,
  Screen,
} from '../ui/kit.js';

const BLANK: DeviceOnlyMember = { first_name: '', last_name: '', email: '', date_of_birth: '' };

/**
 * Claiming takes the same four fields as POST /v1/household/members, because it
 * DELEGATES to that path rather than reimplementing the DOB gate, the
 * guardianship refusal or the seat trigger.
 */
export function ClustersScreen() {
  const clusters = useApi(listClusters, []);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [member, setMember] = useState<DeviceOnlyMember>(BLANK);

  const claim = useAction(claimCluster, () => {
    setClaiming(null);
    setMember(BLANK);
    clusters.reload();
  });
  const dismiss = useAction(dismissCluster, () => clusters.reload());

  return (
    <Screen
      title="Discovered clusters"
      lede="Faces seen repeatedly in your photos that belong to nobody in the household yet. Claiming one creates a member and a seat — the cluster stays unclaimed until a consented enrolment verifies the face."
    >
      <Note>
        <code>FakeClusterClient</code> is still the default in <code>src/http/app.ts</code>: this is
        the one port phase 10 left on a fake, so what appears here comes from the recording fake
        rather than from services.
      </Note>

      <Async state={clusters} empty="no clusters discovered">
        {(data) => (
          <>
            {data.clusters.length === 0 && (
              <p className="empty">no clusters discovered — an empty list, not a 404</p>
            )}
            {data.clusters.map((cluster) => (
              <Panel key={cluster.cluster_id} title={cluster.label}>
                <Row>
                  <Badge tone={cluster.expired ? 'bad' : undefined}>{cluster.status}</Badge>
                  {cluster.expired && <Badge tone="bad">expired</Badge>}
                  <code>{cluster.cluster_id}</code>
                </Row>
                <Row>
                  <Button
                    onClick={() => setClaiming(cluster.cluster_id)}
                    disabled={cluster.expired || claim.pending}
                  >
                    Claim
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => dismiss.run(cluster.cluster_id)}
                    disabled={dismiss.pending}
                  >
                    Dismiss
                  </Button>
                </Row>

                {claiming === cluster.cluster_id && (
                  <div style={{ marginTop: 12 }}>
                    <div className="row">
                      <Field
                        label="first_name"
                        value={member.first_name}
                        onChange={(value) => setMember({ ...member, first_name: value })}
                      />
                      <Field
                        label="last_name"
                        value={member.last_name}
                        onChange={(value) => setMember({ ...member, last_name: value })}
                      />
                      <Field
                        label="email (blank for a minor — their guardian signs)"
                        value={member.email ?? ''}
                        onChange={(value) => setMember({ ...member, email: value })}
                      />
                      <Field
                        label="date_of_birth (YYYY-MM-DD)"
                        value={member.date_of_birth}
                        onChange={(value) => setMember({ ...member, date_of_birth: value })}
                      />
                      <Button
                        onClick={() => claim.run(cluster.cluster_id, member)}
                        disabled={claim.pending}
                      >
                        {claim.pending ? 'claiming…' : 'Confirm claim'}
                      </Button>
                      <Button variant="secondary" onClick={() => setClaiming(null)}>
                        Cancel
                      </Button>
                    </div>
                    <p className="muted" style={{ marginTop: 8 }}>
                      202, not 201 — accepted, not completed. No Idempotency-Key: the unique indexes
                      turn a double-tap into a clean 409 rather than two members.
                    </p>
                  </div>
                )}
              </Panel>
            ))}
            <Raw label="GET /v1/clusters" value={data} />
          </>
        )}
      </Async>

      {claim.error !== null && (
        <Failure
          error={claim.error}
          hint={
            {
              CLUSTER_ALREADY_CLAIMED: 'Someone already claimed this cluster.',
              CLUSTER_EXPIRED: 'The discovery window closed.',
              NOT_YOUR_HOUSEHOLD: 'This cluster belongs to a household you have no seat in.',
              HOUSEHOLD_FULL: 'No free seat to attach the new member to.',
              GUARDIANSHIP_REQUIRED: 'A minor needs a guardian who can sign consent.',
              EMAIL_IN_USE: 'That email already belongs to a person.',
            }[claim.error.code]
          }
        />
      )}
      {claim.result !== null && <Raw label="claim response" value={claim.result} />}
      {dismiss.error !== null && <Failure error={dismiss.error} />}
    </Screen>
  );
}
